<template>
  <div class="message-item ai">
    <div class="message-profile">
      <i v-if="isLoading || isAnswering" class="icon icon--becle-entering"></i>
      <i v-else-if="message.error || hasErrorItem" class="icon icon--becle-crying"></i>
      <i v-else class="icon icon--becle-profile"></i>
    </div>
    <div class="message-text-container">
      <div v-if="isLoading || (isAnswering && iterations.length === 0)" class="mcp-spinner-container">
        <span class="mcp-spinner"></span>
      </div>

      <div v-for="(iter, iterIndex) in iterations" :key="iterIndex" class="mcp-iteration">
        <template v-for="(item, itemIndex) in iter.items">
          <div v-if="item.type === 'thinking' && isActiveItem(iterIndex, itemIndex)" :key="itemIndex" class="mcp-thinking">
            <span class="mcp-thinking-text">{{ getThinkingMessage(item) }}</span>
            <span class="mcp-thinking-dots" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </div>

          <div v-else-if="item.type === 'tool'" :key="'tool-' + itemIndex" :class="['mcp-tool', { expanded: isItemExpanded(iterIndex, itemIndex) }]">
            <button class="mcp-tool-header" @click="toggleItem(iterIndex, itemIndex)">
              <span class="mcp-tool-label">
                <span v-if="isActiveItem(iterIndex, itemIndex)" class="mcp-spinner small"></span>
                <span v-else class="mcp-tool-icon">🔧</span>
                <span class="mcp-tool-name">{{ item.name }}</span>
              </span>
              <i class="toggle-icon icon icon--arrow-dropdown" :class="{ 'expanded': isItemExpanded(iterIndex, itemIndex) }"></i>
            </button>
            <div v-if="item.text" v-show="isItemExpanded(iterIndex, itemIndex)" class="mcp-tool-content">
              <pre>{{ item.text }}</pre>
            </div>
          </div>

          <div v-else-if="item.type === 'text'" :key="'text-' + itemIndex" class='mcp-answer'>
            <div class="mcp-answer-content report-chat-md-content" v-html="getTextHtml(item.content)"></div>
            <div v-if="isActiveItem(iterIndex, itemIndex)" class="mcp-spinner-container"><span class="mcp-spinner big"></span></div>
          </div>
          <p v-if="item.error_code" :key="'is_error-' + itemIndex" class="bold" v-html="$t(`mine.chat.error.${item.error_code}`)"></p>
        </template>
      </div>
      <p v-if="message.error && !hasErrorCodeItem" class="bold" v-html="$t(`mine.chat.error.${message.error}`)" ></p>
    </div>
  </div>
</template>

<script>
import { convertMarkdownToHtml } from '@/libs/utils'

export default {
  name: 'McpMessageContainer',
  props: {
    message: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isAnswering: {
      type: Boolean,
      default: false
    },
    userData: {
      type: Object,
      default: () => ({})
    },
    disableParsing: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      expandedItems: {}
    }
  },
  computed: {
    mcpData () {
      return this.message.mcpData || { iterations: [] }
    },
    iterations () {
      return this.mcpData.iterations || []
    },
    hasErrorItem () {
      return this.iterations.some(iter => {
        return (iter.items || []).some(item => item.is_error)
      })
    },
    hasErrorCodeItem () {
      return this.iterations.some(iter => {
        return (iter.items || []).some(item => item.error_code)
      })
    }
  },
  methods: {
    getTextHtml (text) {
      if (!text) return ''
      return convertMarkdownToHtml(text)
    },
    getThinkingMessage (item) {
      if (!item.thinkingMessage) {
        const messages = [
          this.$t('mine.chat.thinkingMessages.analyzingRequest'),
          this.$t('mine.chat.thinkingMessages.organizingContent'),
          this.$t('mine.chat.thinkingMessages.checkingForAccuracy'),
          this.$t('mine.chat.thinkingMessages.thinkingHard'),
          this.$t('mine.chat.thinkingMessages.preparingAnswer')
        ]
        const randomIndex = Math.floor(Math.random() * messages.length)
        this.$set(item, 'thinkingMessage', messages[randomIndex])
      }
      return item.thinkingMessage
    },
    isActiveItem (iterIndex, itemIndex) {
      if (!this.isAnswering) return false
      const iters = this.iterations
      if (iters.length === 0) return false

      const isLastIter = iterIndex === iters.length - 1
      const isLastItem = itemIndex === iters[iterIndex].items.length - 1
      return isLastIter && isLastItem
    },
    toggleItem (iterIndex, itemIndex) {
      const key = `${iterIndex}-${itemIndex}`
      this.$set(this.expandedItems, key, !this.expandedItems[key])
    },
    isItemExpanded (iterIndex, itemIndex) {
      if (this.isActiveItem(iterIndex, itemIndex)) {
        return true
      }
      const key = `${iterIndex}-${itemIndex}`
      return this.expandedItems[key] || false
    }
  }
}
</script>

<style lang="scss" scoped>
.message-item {
  display: flex;
  width: 100%;
  max-width: 1317px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 12px 20px;
  gap: 12px;
  .message-profile {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #BAC5FF;
    overflow: hidden;
  }

  .message-text-container {
    position: relative;
    flex: 1;
    min-width: 0;
    margin-top: 4px;
    font-size: 14px;
    line-height: 1.5;
    font-weight: 400;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 24px;
    color: #262626;
    word-break: break-all;
    a,
    :-webkit-any-link {
      white-space: normal;
      word-break: break-all;
      text-decoration: underline;
    }
    ul {
      list-style: disc;
    }
    display: flex;
    flex-direction: column;
    gap: 26px;
    border-radius: 10px;
    position: relative;
    margin-top: 6px;

    .mcp-spinner-container {
      display: flex;
      align-items: center;
    }
    .mcp-spinner {
      flex-shrink: 0;
      position: relative;
      width: 20px;
      height: 20px;

      &::before,
      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 2px solid $brandColor;
        transform: translate(-50%, -50%) scale(0);
        animation: ripple 1.5s ease-out infinite;
      }

      &::after {
        animation-delay: 0.5s;
      }

      &.small {
        display: inline-block;
        width: 14px;
        height: 14px;
        margin-right: 6px;
        vertical-align: middle;
      }
      &.big {
        margin: 12px 0;
        width: 36px;
        height: 36px;
      }
    }
    .mcp-iteration {
      display: flex;
      flex-direction: column;
      gap: 12px;
      &:last-child {
        margin-bottom: 0;
      }
      .toggle-icon {
        flex-shrink: 0;
        margin-left: auto;
        &.expanded {
          transform: rotate(180deg);
        }
      }

      .mcp-thinking {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        width: fit-content;
        max-width: 100%;

        &-text {
          color: transparent;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.5;
          background: linear-gradient(
            90deg,
            #888 0%,
            #888 35%,
            #b6bcdd 50%,
            #888 65%,
            #888 100%
          );
          background-size: 220% 100%;
          background-position: 100% 50%;
          background-clip: text;
          -webkit-background-clip: text;
          animation: thinkingSweep 2s linear infinite;
        }

        &-dots {
          display: inline-flex;
          align-items: flex-end;
          gap: 3px;

          span {
            width: 3px;
            height: 3px;
            border-radius: 50%;
            background: #666;
            opacity: 0.2;
            animation: thinkingDots 1.2s ease-in-out infinite;
          }

          span:nth-child(2) {
            animation-delay: 0.2s;
          }

          span:nth-child(3) {
            animation-delay: 0.4s;
          }
        }
      }
      .mcp-tool {
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #ccc;
        background: #f9f9f9;

        &-header {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px;
          border: none;
          cursor: pointer;
          text-align: left;
          font-weight: 500;
          border-bottom: 1px solid #ececec;

          &:hover {
            background: #eeeeee;
          }
          .mcp-tool-label {
            display: flex;
            align-items: center;
            gap: 8px;
            overflow: hidden;
            .mcp-tool-icon {
              flex-shrink: 0;
              font-size: 14px;
              line-height: 1;
              margin-top: 2px;
            }
            .mcp-tool-name {
              color: #444;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }

        &-content {
          padding: 10px;
          background: #fff;

          pre {
            margin: 0;
            background: #ececec;
            padding: 10px;
            border-radius: 4px;
            white-space: pre-wrap;
            word-break: break-word;
            font-size: 12px;
            line-height: 1.4;
            color: #444;
            font-family: 'Consolas', 'Monaco', monospace;
            max-height: 200px;
            overflow-y: auto;
          }
        }
      }
      .mcp-answer {
        &:last-child {
          margin-bottom: 0;
        }

        &-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
          color: #444;
          line-height: 1.6;
        }
      }
    }
  }
}

@keyframes ripple {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0;
  }
}

@keyframes thinkingSweep {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: -100% 50%;
  }
}

@keyframes thinkingDots {
  0%, 100% {
    opacity: 0.2;
  }
  50% {
    opacity: 0.9;
  }
}
</style>
